import ErrorMessage from 'components/ErrorMessage'
import LoggedOutHeader from 'components/LoggedOutHeader'
import { WorkspacePreview } from 'containers/HomeApp/lib/types'
import { User } from 'context/UserContext'
import { Button, Card, majorScale, Pane, Paragraph, Spinner, Strong } from 'evergreen-ui'
import gql from 'graphql-tag'
import { logoutPath, ssoLoginApi, workspacesPath } from 'lib/path-helpers'
import React, { FunctionComponent } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { browserHistory } from 'react-router'

/**
 * Properties required to display the `SSOWorkspaceAccessDeniedCard`.
 *
 * @description
 * We currently must filter a user's workspaces to the currently selected workspace, as no information is given for a workspace with denied access.
 */
interface SSOWorkspaceAccessDeniedCardProps extends Pick<User, 'email'> {
  /** Identifying `slug` of the targeted workspace with a set up SSO connection. */
  workspaceSlug: string
}

/**
 * Expected data result from the needed workspaces GraphQL query.
 */
interface SSOWorkspacePreviewsResponse {
  /** Workspaces with minimally exposed data, used to identify and connect to a workspace with a matching `slug`.  */
  workspaces: WorkspacePreview[]
}

/**
 * Workspaces must currently be gathered and filtered down to the targeted workspace because of needed permissions work.
 *
 * `Access denied` errors block all requested workspace data, not limited to the required SSO connection information.
 */
const listWorkspacePreviewQuery = gql`
  query getWorkspacePreviews {
    workspaces: authzWorkspaces {
      id
      slug
      name
      sso {
        defaultAuth0ConnectionId
      }
    }
  }
`

const navigateToWorkspacesButton = {
  children: 'View my workspaces',
  onClick() {
    browserHistory.replace(workspacesPath())
  }
}

/**
 * Notifies the user of a needed SSO connection for the currently accessed workspace.
 * Facilitates signing into the workspace (with the identifying `workspaceSlug`) with its required SSO connection.
 *
 * @description
 * Can be used to connect a user from user/password credentials to SSO or to switch SSO connections.
 */
export const SSOWorkspaceAccessDeniedCard: FunctionComponent<SSOWorkspaceAccessDeniedCardProps> = ({
  email,
  workspaceSlug
}) => {
  const { loading, error, data } = useQuery<SSOWorkspacePreviewsResponse>(listWorkspacePreviewQuery)
  const SpinnerPane: FunctionComponent = () => (
    <Pane display="flex" justifyContent="center">
      <Spinner size={majorScale(3)} marginX="auto" marginY={majorScale(8)} />
    </Pane>
  )

  if (loading) {
    return <SpinnerPane />
  } else if (error) {
    return (
      <ErrorMessage
        title="Couldn't access workspaces"
        messaging={error.message}
        primaryButton={navigateToWorkspacesButton}
      />
    )
  }

  /**
   * Failure to find a valid `connectionId` will result in an invalid connection attempt.
   * The provided `workspaceSlug` must match a workspace with an established SSO connection.
   * */
  let connectionId: string | undefined
  let currentWorkspace: WorkspacePreview | undefined
  if (data && data.workspaces) {
    currentWorkspace = data.workspaces.find(w => w.slug === workspaceSlug)
    if (currentWorkspace && currentWorkspace.sso) {
      connectionId = currentWorkspace.sso.defaultAuth0ConnectionId
    }
  }

  if (!connectionId) {
    const name = currentWorkspace ? currentWorkspace.name : ''
    const loginPath = `${logoutPath()}?isLoginRequired=true&email=${encodeURIComponent(
      email
    )}&workspaceName=${encodeURIComponent(name)}&redirect=${encodeURIComponent(location.href)}` // Can be current location because URL is original destination in interstitial UI
    browserHistory.replace(loginPath)
    return <SpinnerPane />
  }

  return (
    <Pane flex="1" marginX="auto" marginY={majorScale(4)} maxWidth={majorScale(74)}>
      <Card
        marginX="auto"
        marginY={majorScale(12)}
        paddingX={majorScale(12)}
        paddingY={majorScale(4)}
        position="relative"
        background="white"
        elevation={2}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <LoggedOutHeader title="Log In to Segment" />
        <Paragraph align="center">
          Workspace <Strong>{workspaceSlug}</Strong> uses Single Sign-On for authentication. Continue to log in{' '}
          {email && email.length && (
            <>
              as <Strong>{email}</Strong>
            </>
          )}{' '}
          using your SSO provider.
        </Paragraph>
        <Button
          is="a"
          href={ssoLoginApi({ email, connectionId })}
          height={majorScale(5)}
          marginY={majorScale(3)}
          paddingX={majorScale(8)}
          appearance="primary"
        >
          Continue
        </Button>
      </Card>
    </Pane>
  )
}
